import { scrollToTop } from '@frontend/libs/utils'
import { ChevronLeftIcon } from '@heroicons/react/24/outline'
import { Button } from '@nextui-org/react'
import { useMap } from '@vis.gl/react-google-maps'
import { useEffect, useState } from 'react'

import { type StoreLocator, useStoreLocator } from './lib/useStoreLocator'
import { openDirections } from './lib/util'

interface StoreLocatorListProps {
  selectedStore: null | StoreLocator
  setInfoWindowShown: (value: boolean) => void
  setSelectedStore: (store: null | StoreLocator) => void
  stores: StoreLocator[]
}

export const StoreLocatorList = ({
  selectedStore,
  setInfoWindowShown,
  setSelectedStore,
  stores,
}: StoreLocatorListProps) => {
  const { data, isLoading } = useStoreLocator().getAll()
  const map = useMap('store-locator-map')

  useEffect(() => {
    if (!map) return
    const bounds = new google.maps.LatLngBounds()

    data?.forEach((store) => {
      bounds.extend(
        new google.maps.LatLng(store.location.lat, store.location.lng),
      )
    })
    map.fitBounds(bounds)
  }, [data, map])

  const [userLocation, setUserLocation] = useState<{
    lat: number
    lng: number
  } | null>(null)
  const [distances, setDistances] = useState<{ [key: string]: number }>({})

  useEffect(() => {
    if (userLocation && data) {
      const calculateDistance = (
        lat1: number,
        lng1: number,
        lat2: number,
        lng2: number,
      ) => {
        const toRad = (value: number) => (value * Math.PI) / 180
        const R = 6371 // Radius of the Earth in km
        const dLat = toRad(lat2 - lat1)
        const dLng = toRad(lng2 - lng1)
        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(toRad(lat1)) *
            Math.cos(toRad(lat2)) *
            Math.sin(dLng / 2) *
            Math.sin(dLng / 2)
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
        return R * c // Distance in km
      }

      const newDistances = data.reduce(
        (acc, store) => {
          acc[store.id] = calculateDistance(
            userLocation.lat,
            userLocation.lng,
            store.location.lat,
            store.location.lng,
          )
          return acc
        },
        {} as { [key: string]: number },
      )

      setDistances(newDistances)
    }
  }, [userLocation, data])

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          })
        },
        (error) => {
          console.error('Error getting user location:', error)
        },
      )
    }
  }, [])

  const smoothZoom = (
    map: google.maps.Map,
    targetZoom: number,
    currentZoom: number,
  ) => {
    if (currentZoom >= targetZoom) return
    const zoomStep = 0.5 // Adjust the step size for smoother or faster zoom
    const nextZoom = currentZoom + zoomStep
    map.setZoom(nextZoom)
    setTimeout(() => smoothZoom(map, targetZoom, nextZoom), 100) // Adjust the timeout for smoother or faster zoom
  }

  const handleOnClickStore = (store: StoreLocator) => {
    if (map) {
      map.panTo({
        lat: store.location.lat,
        lng: store.location.lng,
      })
      smoothZoom(map, 18, map.getZoom() || 14) // Start zooming from the current zoom level
    }
    setSelectedStore(store)
    scrollToTop()
  }

  const handleOnClose = () => {
    const bounds = new google.maps.LatLngBounds()

    data?.forEach((store) => {
      bounds.extend(
        new google.maps.LatLng(store.location.lat, store.location.lng),
      )
    })
    map?.fitBounds(bounds)

    if (map) {
      smoothZoom(map, 14, map.getZoom() || 18) // Smoothly zoom out to the default zoom level
    }

    setSelectedStore(null)
    setInfoWindowShown(false)
    scrollToTop()
  }

  if (isLoading) {
    return <div>Loading...</div>
  }

  if (selectedStore) {
    return (
      <div className="relative h-full pb-24 font-normal uppercase md:pb-0">
        <Button
          className="mb-6"
          fullWidth
          onClick={handleOnClose}
          startContent={<ChevronLeftIcon className="h-5 w-5" />}
          variant="bordered"
        >
          Back
        </Button>
        <h3 className="text-lg font-semibold">{selectedStore.name}</h3>
        <p className="text-sm">{selectedStore.location.address}</p>
        {distances[selectedStore.id] !== undefined && (
          <p className="text-sm">
            Distance: {distances[selectedStore.id].toFixed(2)} km
          </p>
        )}
        <p
          className="mt-2 text-sm text-gray-500"
          dangerouslySetInnerHTML={{ __html: selectedStore.content }}
        />
        <Button
          className="mt-4"
          color="primary"
          onClick={() => openDirections(selectedStore.location.address)}
        >
          Directions
        </Button>
      </div>
    )
  }

  return stores?.map((store) => (
    <button
      className="cursor-pointer border-none text-left font-normal uppercase"
      key={store.id}
      onClick={() => handleOnClickStore(store)}
    >
      <h3 className="text-lg font-semibold">{store.name}</h3>
      <p className="text-sm">{store.location.address}</p>
      {distances[store.id] !== undefined && (
        <p className="text-sm">Distance: {distances[store.id].toFixed(2)} km</p>
      )}
      <p
        className="mt-2 text-sm text-gray-500"
        dangerouslySetInnerHTML={{ __html: store.content }}
      />
    </button>
  ))
}

export default StoreLocatorList
